import React, { useState, useEffect } from "react";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { useAuth } from "@/auth/AuthContext";
import { DownloadAnswerButton } from "@/components/DownloadAnswerButton";
import { downloadMainsAnswerPdf } from "@/lib/answerPdf";
import PaywallNotice from "@/components/PaywallNotice";
import HandwrittenUpload from "@/components/HandwrittenUpload";
import GSPYQBank from "@/components/mains/GSPYQBank";
import { AIOutputDisclaimer } from "@/components/LegalDisclaimers";
import {
  BookOpen, Target, Award, TrendingUp, ArrowRight, Sparkles,
  FileText, MapPin, BarChart3, CircleCheck, AlertCircle, Lightbulb, Lock, Camera, ArrowLeft,
  Search, Pencil, CheckCircle2
} from "lucide-react";

// GS-I Diagram Components

// 1. Flowchart Diagram - Shows historical timeline/evolution
const FlowchartDiagram = ({ answer }) => {
  const mainTopic = answer.diagram?.type || "Historical Evolution";
  const stages = answer.body?.slice(0, 5).map(b => b.heading) || ["Ancient", "Medieval", "Colonial", "Post-Independence", "Modern"];
  
  return (
    <div className="border-2 border-amber-200 rounded-lg p-6 bg-gradient-to-br from-amber-50 to-orange-50">
      <h4 className="text-sm font-bold text-amber-800 mb-4 text-center">📊 Timeline Flowchart</h4>
      <div className="flex flex-col items-center space-y-3">
        <div className="bg-amber-600 text-white rounded-lg px-6 py-3 shadow-md">
          <p className="text-sm font-bold">{mainTopic}</p>
        </div>
        <div className="flex items-center justify-center w-full flex-wrap gap-2">
          {stages.map((stage, idx) => (
            <React.Fragment key={idx}>
              <div className={`flex-1 min-w-24 max-w-32 border-2 rounded-lg px-3 py-2 text-center ${
                idx === 0 ? 'bg-amber-100 border-amber-300' :
                idx === 1 ? 'bg-orange-100 border-orange-300' :
                idx === 2 ? 'bg-red-100 border-red-300' :
                idx === 3 ? 'bg-rose-100 border-rose-300' :
                'bg-pink-100 border-pink-300'
              }`}>
                <p className="text-[10px] font-semibold">{stage}</p>
              </div>
              {idx < stages.length - 1 && (
                <div className="text-amber-500 mx-1">→</div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
      {answer.diagram?.description && (
        <p className="text-xs text-slate-600 mt-4 text-center italic">{answer.diagram.description}</p>
      )}
    </div>
  );
};

// 2. Hub & Spoke Diagram - Shows cultural/geographical connections
const HubAndSpokeDiagram = ({ answer }) => {
  const centerTopic = answer.diagram?.type || "Cultural Heritage";
  const spokes = answer.body?.slice(0, 6).map(b => b.heading) || ["Architecture", "Literature", "Music", "Dance", "Sculpture", "Paintings"];
  const colors = [
    'bg-red-100 border-red-400',
    'bg-orange-100 border-orange-400',
    'bg-amber-100 border-amber-400',
    'bg-emerald-100 border-emerald-400',
    'bg-blue-100 border-blue-400',
    'bg-purple-100 border-purple-400'
  ];
  
  return (
    <div className="border-2 border-orange-200 rounded-lg p-6 bg-gradient-to-br from-orange-50 to-amber-50">
      <h4 className="text-sm font-bold text-orange-800 mb-4 text-center">🎯 Hub & Spoke Diagram</h4>
      <div className="relative flex items-center justify-center" style={{ minHeight: '280px' }}>
        {/* Center Hub */}
        <div className="absolute z-10 bg-gradient-to-br from-orange-500 to-amber-600 text-white rounded-full w-24 h-24 flex items-center justify-center shadow-lg">
          <p className="text-[10px] font-bold text-center px-2">{centerTopic}</p>
        </div>
        
        {/* Spokes in circle */}
        {spokes.map((spoke, idx) => {
          const angle = (idx * 360) / spokes.length - 90;
          const rad = (angle * Math.PI) / 180;
          const x = Math.cos(rad) * 100;
          const y = Math.sin(rad) * 100;
          
          return (
            <React.Fragment key={idx}>
              <div
                className="absolute w-16 h-0.5 bg-orange-300 origin-left"
                style={{
                  left: '50%',
                  top: '50%',
                  transform: `rotate(${angle + 90}deg) translateY(-50%)`,
                }}
              />
              <div
                className={`absolute border-2 rounded-lg px-3 py-2 shadow-sm ${colors[idx % colors.length]}`}
                style={{
                  left: `calc(50% + ${x}px)`,
                  top: `calc(50% + ${y}px)`,
                  transform: 'translate(-50%, -50%)',
                  width: '85px'
                }}
              >
                <p className="text-[9px] font-semibold text-center">{spoke}</p>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      {answer.diagram?.description && (
        <p className="text-xs text-slate-600 mt-4 text-center italic">{answer.diagram.description}</p>
      )}
    </div>
  );
};

// 3. Table Diagram - Shows comparison (historical periods, geographical regions)
const TableDiagram = ({ answer }) => {
  const rows = answer.body?.slice(0, 5) || [];
  
  return (
    <div className="border-2 border-amber-200 rounded-lg p-4 bg-gradient-to-br from-amber-50 to-yellow-50">
      <h4 className="text-sm font-bold text-amber-800 mb-4 text-center">📋 Comparison Table</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-amber-600 text-white">
              <th className="p-2 text-left font-bold border border-amber-700">Aspect</th>
              <th className="p-2 text-left font-bold border border-amber-700">Historical Context</th>
              <th className="p-2 text-left font-bold border border-amber-700">Significance</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-amber-50'}>
                <td className="p-2 border border-amber-200 font-semibold text-amber-900">
                  {row.heading}
                </td>
                <td className="p-2 border border-amber-200 text-slate-700">
                  {row.points?.[0]?.explanation || 'Key detail'}
                </td>
                <td className="p-2 border border-amber-200 text-slate-700">
                  {row.points?.[0]?.example || 'Impact'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {answer.diagram?.description && (
        <p className="text-xs text-slate-600 mt-4 text-center italic">{answer.diagram.description}</p>
      )}
    </div>
  );
};

// 4. Pyramid Diagram - Shows social hierarchy or geographical layers
const PyramidDiagram = ({ answer }) => {
  const levels = answer.body?.slice(0, 4).map(b => b.heading) || ["Elite/Rulers", "Nobility/Officials", "Middle Class", "Common People"];
  const widths = ['w-40', 'w-56', 'w-72', 'w-88'];
  const colors = [
    'bg-amber-600 text-white',
    'bg-amber-500 text-white',
    'bg-amber-400 text-white',
    'bg-amber-300 text-amber-900'
  ];
  
  return (
    <div className="border-2 border-amber-200 rounded-lg p-6 bg-gradient-to-br from-amber-50 to-orange-50">
      <h4 className="text-sm font-bold text-amber-800 mb-4 text-center">🔺 Pyramid Diagram (Hierarchy)</h4>
      <div className="flex flex-col items-center space-y-1">
        {levels.map((level, idx) => (
          <div 
            key={idx} 
            className={`${widths[idx]} ${colors[idx]} rounded-lg py-3 px-4 text-center shadow-md transition-all hover:scale-105`}
            style={{ clipPath: idx === 0 ? 'polygon(20% 0%, 80% 0%, 100% 100%, 0% 100%)' : 'none' }}
          >
            <p className="text-xs font-bold">{level}</p>
          </div>
        ))}
      </div>
      <div className="flex justify-center mt-4">
        <div className="text-xs text-amber-600 font-medium">↑ Social/Historical Hierarchy ↓</div>
      </div>
      {answer.diagram?.description && (
        <p className="text-xs text-slate-600 mt-4 text-center italic">{answer.diagram.description}</p>
      )}
    </div>
  );
};

// 5. Venn Diagram - Shows overlapping influences/cultures
const VennDiagram = ({ answer }) => {
  const circles = answer.body?.slice(0, 3).map(b => b.heading) || ["Indian Culture", "Persian Influence", "European Impact"];
  
  return (
    <div className="border-2 border-rose-200 rounded-lg p-6 bg-gradient-to-br from-rose-50 to-pink-50">
      <h4 className="text-sm font-bold text-rose-800 mb-4 text-center">⭕ Venn Diagram (Cultural Overlap)</h4>
      <div className="relative flex items-center justify-center" style={{ minHeight: '220px' }}>
        {/* Three overlapping circles */}
        <div className="absolute w-32 h-32 rounded-full bg-amber-200/60 border-2 border-amber-400 flex items-center justify-center"
          style={{ left: 'calc(50% - 80px)', top: '20px' }}>
          <p className="text-[10px] font-bold text-amber-900 text-center px-2 mt-8">{circles[0]}</p>
        </div>
        <div className="absolute w-32 h-32 rounded-full bg-orange-200/60 border-2 border-orange-400 flex items-center justify-center"
          style={{ left: 'calc(50% + 10px)', top: '20px' }}>
          <p className="text-[10px] font-bold text-orange-900 text-center px-2 mt-8">{circles[1]}</p>
        </div>
        <div className="absolute w-32 h-32 rounded-full bg-rose-200/60 border-2 border-rose-400 flex items-center justify-center"
          style={{ left: 'calc(50% - 35px)', top: '80px' }}>
          <p className="text-[10px] font-bold text-rose-900 text-center px-2 mt-12">{circles[2]}</p>
        </div>
        
        {/* Center overlap */}
        <div className="absolute bg-amber-600 text-white rounded-full w-16 h-16 flex items-center justify-center shadow-lg z-10"
          style={{ left: 'calc(50% - 32px)', top: '65px' }}>
          <p className="text-[8px] font-bold text-center">Synthesis</p>
        </div>
      </div>
      {answer.diagram?.description && (
        <p className="text-xs text-slate-600 mt-4 text-center italic">{answer.diagram.description}</p>
      )}
    </div>
  );
};

const GS1_SUBJECTS = [
  { label: "Indian Society", value: "indian_society" },
  { label: "World History", value: "world_history" },
  { label: "Indian History", value: "indian_history" },
  { label: "Indian Culture", value: "indian_culture" },
  { label: "Geography", value: "geography" }
];

const GS1SuitePage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [mode, setMode] = useState("home"); // home, answer-writing, evaluation, expected-questions, pyq-pattern
  const [loading, setLoading] = useState(false);
  
  // Answer Writing state
  const [question, setQuestion] = useState("");
  const [marks, setMarks] = useState(10);
  const [wordLimit, setWordLimit] = useState(150);
  const [selectedDiagram, setSelectedDiagram] = useState("Flowchart");
  const [answer, setAnswer] = useState(null);
  
  // Evaluation state
  const [evalQuestion, setEvalQuestion] = useState("");
  const [evalAnswer, setEvalAnswer] = useState("");
  const [evaluation, setEvaluation] = useState(null);
  const [evalInputMode, setEvalInputMode] = useState("type"); // "type" or "handwritten"
  
  // Expected Questions state
  const [subject, setSubject] = useState("indian_history");
  const [expectedData, setExpectedData] = useState(null);
  const [practicedIds, setPracticedIds] = useState([]);
  
  // PYQ Pattern state
  const [pyqSubject, setPyqSubject] = useState("indian_history");
  const [pyqData, setPyqData] = useState(null);
  const [pyqYearRange, setPyqYearRange] = useState("5"); // 5, 10, 15, 20 years
  
  // Answer usage tracking for FREE users (per suite, lifetime)
  const [answerUsage, setAnswerUsage] = useState({ 
    is_pro: false, 
    suite: "GS1",
    free_full_answer_used: false, 
    answer_type: "full",
    message: ""
  });

  const isPro = user?.role === "PRO";
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const handleDownloadPdf = async () => {
    if (!isPro) {
      toast({ description: "Saving answers as PDF is a PRO feature. Upgrade to download." });
      return;
    }
    setDownloadingPdf(true);
    try {
      await downloadMainsAnswerPdf({
        suite: "GS1", question, marks, wordLimit, answer,
        diagramElementId: "gs1-answer-diagram",
        diagramLabel: diagramTypes.find((d) => d.id === selectedDiagram)?.label,
      });
      toast({ description: "PDF downloaded." });
    } catch (e) {
      toast({ variant: "destructive", description: "Could not generate PDF." });
    } finally {
      setDownloadingPdf(false);
    }
  };
  const isFree = user?.role === "FREE";

  // Fetch answer usage on mount for GS1 suite
  useEffect(() => {
    const fetchUsage = async () => {
      try {
        const { data } = await managedAxios.get(`${API_BASE}/answers/usage?suite=GS1`);
        setAnswerUsage(data);
      } catch (err) {
        console.error("Failed to fetch answer usage:", err);
      }
    };
    fetchUsage();
  }, []);

  const diagramTypes = [
    { id: "Flowchart", label: "Flowchart", icon: "📊", description: "Shows timeline/evolution" },
    { id: "HubSpoke", label: "Hub & Spoke", icon: "🎯", description: "Cultural connections" },
    { id: "Table", label: "Table", icon: "📋", description: "Historical comparison" },
    { id: "Pyramid", label: "Pyramid", icon: "🔺", description: "Social hierarchy" },
    { id: "Venn", label: "Venn", icon: "⭕", description: "Cultural overlaps" },
  ];

  // Render diagram based on selected type
  const renderDiagram = () => {
    if (!answer) return null;
    
    switch (selectedDiagram) {
      case "HubSpoke":
        return <HubAndSpokeDiagram answer={answer} />;
      case "Table":
        return <TableDiagram answer={answer} />;
      case "Pyramid":
        return <PyramidDiagram answer={answer} />;
      case "Venn":
        return <VennDiagram answer={answer} />;
      case "Flowchart":
      default:
        return <FlowchartDiagram answer={answer} />;
    }
  };

  // Answer Writing Functions
  const handleGenerateAnswer = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/answers/mains`, {
        question,
        paper: "GS1",
        marks,
        word_limit: wordLimit,
      });
      
      // Show the answer FIRST
      setAnswer(data);
      toast({ description: "GS-I Answer generated successfully!" });
      
      // For FREE users: Wait 10 seconds AFTER showing answer, then update usage
      if (!isPro && data.usage) {
        setTimeout(() => {
          setAnswerUsage({
            is_pro: false,
            suite: "GS1",
            free_full_answer_used: true,
            answer_type: data.usage?.answer_type || "partial",
            message: data.paywall_message || ""
          });
          if (data.paywall_message) {
            toast({ 
              description: data.paywall_message,
              duration: 5000
            });
          }
        }, 10000); // 10 seconds delay
      } else if (data.usage || data.quota) {
        // PRO users: reflect the live monthly quota returned by the backend
        const q = data.quota;
        setAnswerUsage({
          is_pro: true,
          suite: "GS1",
          free_full_answer_used: false,
          answer_type: "full",
          unlimited: !q,
          limit: q?.limit ?? 10,
          used: q?.used,
          remaining: q?.remaining,
          message: data.paywall_message || (q ? `${q.remaining}/${q.limit} answers left this cycle` : "Unlimited"),
        });
        if (data.paywall_message) {
          toast({ description: data.paywall_message, duration: 6000 });
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (err?.response?.status === 403 && msg) {
        toast({ variant: "destructive", description: msg });
      } else {
        toast({ variant: "destructive", description: msg || "Could not generate answer." });
      }
    } finally {
      setLoading(false);
    }
  };

  // Evaluation Functions
  const handleEvaluate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/eval/mains`, {
        question: evalQuestion,
        answer: evalAnswer,
        marks: marks,
      });
      setEvaluation(data);
      toast({ description: "GS-I Evaluation generated." });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (err?.response?.status === 403 && msg) {
        toast({ variant: "destructive", description: msg });
      } else {
        toast({ variant: "destructive", description: msg || "Could not evaluate answer." });
      }
    } finally {
      setLoading(false);
    }
  };

  // Expected Questions Functions
  const fetchExpectedQuestions = async () => {
    setLoading(true);
    try {
      const res = await managedAxios.post(`${API_BASE}/expected/generate`, {
        paper: "GS1",
        subject: subject,
        num_questions: 15
      });
      setExpectedData(res.data);
      setPracticedIds([]);
      toast({ description: "GS-I Expected questions generated!" });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast({ variant: "destructive", description: msg || "Could not load expected questions." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mode === "expected-questions") {
      fetchExpectedQuestions();
    }
    if (mode === "pyq-pattern") {
      fetchPyqPattern();
    }
  }, [subject, mode, pyqSubject, pyqYearRange]);

  // PYQ Pattern Functions
  const fetchPyqPattern = async () => {
    setLoading(true);
    try {
      const res = await managedAxios.post(`${API_BASE}/pyq/analyze`, {
        paper: "GS1",
        subject: pyqSubject,
        year_range: parseInt(pyqYearRange)
      });
      setPyqData(res.data);
      toast({ description: "GS-I PYQ Pattern analysis generated!" });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast({ variant: "destructive", description: msg || "Could not load PYQ pattern." });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPracticed = async (questionId) => {
    try {
      await managedAxios.post(`${API_BASE}/practice/expected`, {
        question_id: questionId,
        state: "GS1",
        bucket: subject,
      });
      setPracticedIds((prev) => (prev.includes(questionId) ? prev : [...prev, questionId]));
    } catch (e) {
      // soft fail
    }
  };

  const handleSendToMains = (questionText, questionId) => {
    if (questionId) {
      handleMarkPracticed(questionId);
    }
    setQuestion(questionText);
    setMode("answer-writing");
  };

  // CHAPTERWISE PYQ BANK VIEW
  if (mode === "pyq-bank") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50/30 to-amber-50/30">
        <GSPYQBank
          paper="GS1"
          onBack={() => setMode("home")}
          onPractice={(q, m) => {
            setQuestion(q || "");
            if (m) setMarks(m);
            setMode("answer-writing");
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      </div>
    );
  }


  // HOME VIEW
  if (mode === "home") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50/30 to-amber-50/30">
        
        {/* ═══════════════════════════════════════════════════════════
            VIBRANT HEADER - Orange/Amber Theme for GS-I (History/Culture)
            ═══════════════════════════════════════════════════════════ */}
        <div className="bg-gradient-to-r from-orange-500 via-amber-500 to-red-500 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
            {/* Back button & Title */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => navigate("/mains")}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold flex items-center gap-2">
                    <BookOpen className="w-7 h-7" />
                    GS-I Suite
                  </h1>
                  <p className="text-orange-100 text-sm">History • Geography • Society • Culture</p>
                </div>
              </div>
              
              {/* PRO Badge */}
              <Badge className={`px-3 py-1.5 ${isPro 
                ? 'bg-white/20 text-white border-0' 
                : 'bg-white/10 text-orange-100 border-0'}`}>
                {isPro ? '⭐ PRO' : '🆓 FREE'}
              </Badge>
            </div>

            {/* Quick Stats Row */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                <p className="text-xl font-bold">{isPro ? (answerUsage.unlimited ? '∞' : (answerUsage.remaining ?? answerUsage.limit ?? 10)) : answerUsage.free_full_answer_used ? '0' : '1'}</p>
                <p className="text-[10px] text-orange-200">{isPro && !answerUsage.unlimited ? 'Answers Left' : 'Full Answers'}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                <p className="text-xl font-bold">∞</p>
                <p className="text-[10px] text-orange-200">Evaluations</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                <p className="text-xl font-bold">20+</p>
                <p className="text-[10px] text-orange-200">Years PYQ</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
          
          {/* ═══════════════════════════════════════════════════════════
              TOPPER TIP BANNER
              ═══════════════════════════════════════════════════════════ */}
          <Card className="border-0 shadow-lg bg-gradient-to-r from-orange-100 to-amber-100 overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-orange-900">GS-I Focus Areas</p>
                  <p className="text-sm text-orange-700">Art & Culture, Post-independence, World History & Geography patterns</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ═══════════════════════════════════════════════════════════
              MAIN FEATURES GRID
              ═══════════════════════════════════════════════════════════ */}
          <div className="grid grid-cols-2 gap-4">
            {/* Answer Writing */}
            <Card 
              className="border-2 border-orange-200 bg-gradient-to-br from-orange-500/10 to-amber-500/10 cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all group overflow-hidden"
              onClick={() => setMode("answer-writing")}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <BookOpen className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold font-heading text-lg text-slate-800">Answer Writing</h3>
                      {!isPro && (
                        <Badge className={`text-xs ${!answerUsage.free_full_answer_used ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {!answerUsage.free_full_answer_used ? '1 FREE' : 'Partial'}
                        </Badge>
                      )}
                      {isPro && !answerUsage.unlimited && typeof answerUsage.remaining === 'number' && (
                        <Badge className={`text-xs ${answerUsage.remaining > 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {answerUsage.remaining}/{answerUsage.limit || 10} left
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">Generate GS-I model answers with diagrams</p>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-4 pt-3 border-t border-slate-200/50">
                  <div className="flex items-center gap-1 text-sm font-medium text-orange-600">
                    Start Writing
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Smart Evaluation */}
            <Card 
              className="border-2 border-purple-200 bg-gradient-to-br from-purple-500/10 to-pink-500/10 cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all group overflow-hidden"
              onClick={() => setMode("evaluation")}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <Award className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold font-heading text-lg text-slate-800">Smart Evaluation</h3>
                    <p className="text-sm text-slate-600 mt-1">Get examiner-aligned feedback on your answers</p>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-4 pt-3 border-t border-slate-200/50">
                  <div className="flex items-center gap-1 text-sm font-medium text-purple-600">
                    Evaluate
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Expected Questions */}
            <Card 
              className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all group overflow-hidden"
              onClick={() => setMode("expected-questions")}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <Target className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold font-heading text-lg text-slate-800">Trend-Based Questions</h3>
                    <p className="text-sm text-slate-600 mt-1">Predicted from 20+ years of UPSC PYQ patterns</p>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-4 pt-3 border-t border-slate-200/50">
                  <div className="flex items-center gap-1 text-sm font-medium text-emerald-600">
                    Explore
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* PYQ Pattern */}
            <Card 
              className="border-2 border-amber-200 bg-gradient-to-br from-amber-500/10 to-yellow-500/10 cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all group overflow-hidden"
              onClick={() => setMode("pyq-pattern")}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <TrendingUp className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold font-heading text-lg text-slate-800">PYQ Pattern</h3>
                    <p className="text-sm text-slate-600 mt-1">20+ years analysis with trend forecasting</p>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-4 pt-3 border-t border-slate-200/50">
                  <div className="flex items-center gap-1 text-sm font-medium text-amber-600">
                    Analyze
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Chapterwise PYQ Bank */}
          <Card
            className="border-2 border-orange-200 bg-gradient-to-br from-orange-500/10 to-amber-500/10 cursor-pointer hover:shadow-xl hover:scale-[1.01] transition-all group overflow-hidden"
            onClick={() => setMode("pyq-bank")}
            data-testid="gs-pyq-open-card"
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                  <FileText className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold font-heading text-lg text-slate-800">Chapterwise PYQ Bank</h3>
                    <Badge className="text-xs bg-green-100 text-green-700">FREE</Badge>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">Every GS-I mains question (2013-2026) mapped chapter & subtopic-wise with answer approach</p>
                </div>
                <ArrowRight className="w-5 h-5 text-orange-500" />
              </div>
            </CardContent>
          </Card>


          {/* ═══════════════════════════════════════════════════════════
              QUICK FEATURES ROW
              ═══════════════════════════════════════════════════════════ */}
          <div className="grid grid-cols-3 gap-3">
            <Card className="border border-slate-200 bg-white">
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-md">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <p className="font-medium text-sm text-slate-800">Historical Depth</p>
                <p className="text-[10px] text-slate-500">Analysis over narration</p>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 bg-white">
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-md">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <p className="font-medium text-sm text-slate-800">Spatial Clarity</p>
                <p className="text-[10px] text-slate-500">Map-first approach</p>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 bg-white">
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-md">
                  <Lightbulb className="w-5 h-5 text-white" />
                </div>
                <p className="font-medium text-sm text-slate-800">Sociological Lens</p>
                <p className="text-[10px] text-slate-500">Diversity perspectives</p>
              </CardContent>
            </Card>
          </div>

          {/* PRO Upgrade CTA for FREE users */}
          {!isPro && (
            <Card 
              className="border-2 border-orange-300 bg-gradient-to-r from-orange-50 to-amber-50 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => navigate("/upgrade")}
            >
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Award className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold font-heading text-lg text-slate-800">Upgrade to CLARIAS PRO</p>
                    <p className="text-sm text-slate-600">Unlock unlimited GS-I answers with diagrams & full evaluation</p>
                  </div>
                  <ArrowRight className="w-6 h-6 text-orange-500" />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Footer Quote */}
          <div className="text-center pt-4 pb-8">
            <p className="text-sm text-slate-400 italic">&ldquo;History is not the past. It is the present.&rdquo; — James Baldwin</p>
          </div>
        </div>
      </div>
    );
  }

  // ANSWER WRITING VIEW
  if (mode === "answer-writing") {
    return (
      <div className="min-h-screen bg-slate-50/30">
      {/* Premium Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-72 h-72 bg-blue-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-cyan-100/40 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-4xl mx-auto px-6 py-10 space-y-8">
        <div className="flex items-center justify-between">
          <Button variant="outline" className="rounded-full hover:bg-slate-100" onClick={() => setMode("home")}>← Back</Button>
          <Badge className={`px-3 py-1 ${isPro 
            ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white border-0' 
            : 'bg-slate-100 text-slate-600 border-0'}`}>
            {isPro ? <><Award className="w-3 h-3 mr-1" /> PRO</> : 'FREE'}
          </Badge>
        </div>

        <div className="glass-card rounded-2xl p-6 animate-fade-up">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-slate-900">GS-I Answer Writing</h1>
            {!isPro && (
              <Badge className={`text-sm px-3 py-1 ${!answerUsage.free_full_answer_used ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                {!answerUsage.free_full_answer_used ? '1 FREE available' : 'Partial mode'}
              </Badge>
            )}
            {isPro && (
              <Badge className="bg-blue-100 text-blue-700 text-sm px-3 py-1">
                {answerUsage.unlimited ? 'Unlimited' : `${answerUsage.remaining ?? answerUsage.limit ?? 10}/${answerUsage.limit || 10} answers left this cycle`}
              </Badge>
            )}
          </div>
          <p className="text-slate-600 mt-2">
            History • Geography • Society • Culture
          </p>
        </div>

        <Card className="border-slate-200 bg-white">
          <CardContent className="p-6">
            <form className="space-y-6" onSubmit={handleGenerateAnswer}>
              <div className="space-y-3">
                <label className="text-sm font-semibold text-slate-700">QUESTION</label>
                <Textarea
                  rows={4}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Enter your GS-I question (History, Geography, Society, Culture)..."
                  required
                  className="text-sm resize-none border-2 focus:border-orange-400"
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-slate-700">MARKS</label>
                  <div className="flex gap-2">
                    {[10, 15, 20, 25].map((m) => (
                      <Button
                        key={m}
                        type="button"
                        variant={marks === m ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          setMarks(m);
                          setWordLimit(m * 15);
                        }}
                        className={marks === m ? "bg-orange-500 hover:bg-orange-600" : ""}
                      >
                        {m}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-semibold text-slate-700">WORD LIMIT</label>
                  <input
                    type="number"
                    value={wordLimit}
                    onChange={(e) => setWordLimit(parseInt(e.target.value))}
                    className="w-full px-3 py-2 text-sm border-2 border-slate-200 rounded-md focus:border-orange-400 focus:outline-none"
                    min="50"
                    max="500"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-sm font-semibold text-slate-700">SELECT DIAGRAM TYPE FOR YOUR ANSWER</label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {diagramTypes.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => setSelectedDiagram(d.id)}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        selectedDiagram === d.id 
                          ? "bg-amber-500 border-amber-600 text-white shadow-md" 
                          : "bg-white border-slate-200 hover:border-amber-300 hover:bg-amber-50"
                      }`}
                    >
                      <div className="text-2xl mb-1">{d.icon}</div>
                      <div className={`text-xs font-bold ${selectedDiagram === d.id ? 'text-white' : 'text-slate-800'}`}>
                        {d.label}
                      </div>
                      <div className={`text-[9px] mt-1 ${selectedDiagram === d.id ? 'text-amber-100' : 'text-slate-500'}`}>
                        {d.description}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className={`w-full font-semibold py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white`}
              >
                {loading ? "Generating GS-I Answer..." : 
                  (!isPro && answerUsage.free_full_answer_used) 
                    ? "📋 Generate Partial Answer" 
                    : "🚀 Generate GS-I Answer"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Display Answer */}
        {answer && (
          <div className="space-y-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="text-base font-bold text-slate-900">Model Answer</h2>
              <DownloadAnswerButton locked={!isPro} busy={downloadingPdf} onClick={handleDownloadPdf} testid="gs1-download-pdf" />
            </div>
            {answer.interpretation && (
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">Q</div>
                    <h3 className="text-lg font-bold text-purple-900 uppercase">Question Interpretation</h3>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <p className="text-sm text-slate-800">{answer.interpretation}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {answer.intro && (
              <Card className="border-emerald-200 bg-emerald-50/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">1</div>
                    <h3 className="text-lg font-bold text-purple-900 uppercase">Introduction</h3>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-emerald-200">
                    <p className="text-sm text-slate-800 leading-relaxed">{answer.intro}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {answer.diagram && (
              <Card className="border-amber-200 bg-amber-50/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-amber-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">2</div>
                    <h3 className="text-lg font-bold text-amber-900 uppercase">Diagram: {diagramTypes.find(d => d.id === selectedDiagram)?.label}</h3>
                  </div>
                  <div id="gs1-answer-diagram" className="bg-white rounded-lg">{renderDiagram()}</div>
                </CardContent>
              </Card>
            )}

            {answer.body && answer.body.length > 0 && (
              <Card className="border-pink-200 bg-pink-50/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">3</div>
                    <h3 className="text-lg font-bold text-purple-900 uppercase">Body</h3>
                  </div>

                  <div className="space-y-5">
                    {answer.body.map((section, idx) => (
                      <div key={idx} className="space-y-3">
                        <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-4 border border-purple-200">
                          <h4 className="text-sm font-bold text-purple-900 mb-2">
                            Part {String.fromCharCode(65 + idx)}: {section.heading}
                          </h4>
                        </div>

                        {section.points && section.points.map((point, pidx) => (
                          <div key={pidx} className="bg-white rounded-xl border-2 border-slate-200 p-5 space-y-4">
                            <div>
                              <h5 className="text-sm font-bold text-slate-900 uppercase mb-2">{point.keyword}</h5>
                              <p className="text-sm text-slate-700 leading-relaxed">{point.explanation}</p>
                            </div>

                            {point.example && (
                              <div className="bg-blue-50 border-l-4 border-blue-400 rounded-r-lg p-3 flex items-start gap-2">
                                <MapPin className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-xs font-semibold text-blue-900 mb-1">Example</p>
                                  <p className="text-xs text-blue-800">{point.example}</p>
                                </div>
                              </div>
                            )}

                            {point.stat && (
                              <div className="bg-green-50 border-l-4 border-green-400 rounded-r-lg p-3 flex items-start gap-2">
                                <BarChart3 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-xs font-semibold text-green-900 mb-1">Data</p>
                                  <p className="text-xs text-green-800">{point.stat}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {answer.way_forward && answer.way_forward.length > 0 && (
              <Card className="border-yellow-200 bg-yellow-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">4</div>
                    <h3 className="text-lg font-bold text-purple-900 uppercase">Way Forward</h3>
                  </div>
                  <div className="bg-white border-2 border-yellow-300 rounded-xl p-5">
                    <ol className="space-y-3">
                      {answer.way_forward.map((item, idx) => (
                        <li key={idx} className="flex gap-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-amber-100 text-amber-900 rounded-full flex items-center justify-center text-xs font-bold">
                            {idx + 1}
                          </span>
                          <span className="text-sm text-slate-800">{item}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </CardContent>
              </Card>
            )}

            {answer.conclusion && (
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">5</div>
                    <h3 className="text-lg font-bold text-purple-900 uppercase">Conclusion</h3>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <p className="text-sm text-slate-800 leading-relaxed">{answer.conclusion}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {answer.alternates?.intros && answer.alternates.intros.length > 0 && (
              <Card className="border-indigo-200 bg-indigo-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">6</div>
                    <h3 className="text-lg font-bold text-indigo-900 uppercase">Alternative Introductions</h3>
                  </div>
                  <div className="space-y-3">
                    {answer.alternates.intros.map((intro, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-4 border border-indigo-200">
                        <p className="text-xs font-semibold text-indigo-700 mb-2">Option {idx + 1}</p>
                        <p className="text-sm text-slate-800">{intro}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {answer.alternates?.conclusions && answer.alternates.conclusions.length > 0 && (
              <Card className="border-fuchsia-200 bg-fuchsia-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-fuchsia-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">7</div>
                    <h3 className="text-lg font-bold text-fuchsia-900 uppercase">Alternative Conclusions</h3>
                  </div>
                  <div className="space-y-3">
                    {answer.alternates.conclusions.map((conclusion, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-4 border border-fuchsia-200">
                        <p className="text-xs font-semibold text-fuchsia-700 mb-2">Option {idx + 1}</p>
                        <p className="text-sm text-slate-800">{conclusion}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {answer.topper_notes?.bullets && answer.topper_notes.bullets.length > 0 && (
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-amber-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">8</div>
                    <h3 className="text-lg font-bold text-amber-900 uppercase">Topper Notes</h3>
                  </div>
                  <div className="bg-white rounded-xl border-2 border-amber-200 p-5">
                    <ul className="space-y-2">
                      {answer.topper_notes.bullets.map((note, idx) => (
                        <li key={idx} className="flex gap-3 items-start">
                          <span className="flex-shrink-0 w-5 h-5 bg-amber-100 text-amber-900 rounded-full flex items-center justify-center text-xs font-bold">★</span>
                          <span className="text-sm text-slate-800">{note}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            )}

            {answer.checklist && answer.checklist.length > 0 && (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">9</div>
                    <h3 className="text-lg font-bold text-green-900 uppercase">
                      Checklist (Must Include for {marks}+ Marks)
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {answer.checklist.map((item, idx) => (
                      <div key={idx} className="bg-white border border-green-200 rounded-lg p-3 flex items-center gap-3">
                        {item.present ? (
                          <CircleCheck className="w-5 h-5 text-green-600 flex-shrink-0" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        )}
                        <span className="text-sm text-slate-800">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Score Estimate - Always out of 10 */}
            {answer.score_estimate !== undefined && (
              <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold">
                        <Award className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-blue-900">Score Estimate</h3>
                        <p className="text-xs text-blue-600">Based on UPSC standards</p>
                      </div>
                    </div>
                    <div className="text-center bg-white rounded-xl px-4 py-2 border-2 border-blue-200">
                      <p className="text-3xl font-bold text-blue-900">{answer.score_estimate}/10</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {answer.paywall_message && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center">
                <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Upgrade to PRO</h3>
                <p className="text-sm text-slate-600">{answer.paywall_message}</p>
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    );
  }

  // EVALUATION VIEW
  if (mode === "evaluation") {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setMode("home")}>← Back to GS-I Suite</Button>
          <Badge className="px-4 py-2 text-sm">{isPro ? "⭐ PRO" : "🆓 FREE"}</Badge>
        </div>

        <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-2xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
                <Sparkles className="w-8 h-8" />
                GS-I Smart Evaluation
              </h1>
              <p className="text-orange-50">
                Get GS-I specific feedback on History, Geography, Society & Culture answers
              </p>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <FileText className="w-5 h-5 text-blue-600" />
                <CircleCheck className="w-4 h-4 text-blue-600" />
              </div>
              <p className="text-xs font-semibold text-blue-900">Structure Audit</p>
              <p className="text-[11px] text-blue-700">Flow & organization check</p>
            </CardContent>
          </Card>

          <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <Target className="w-5 h-5 text-emerald-600" />
                <TrendingUp className="w-4 h-4 text-emerald-600" />
              </div>
              <p className="text-xs font-semibold text-emerald-900">Depth Analysis</p>
              <p className="text-[11px] text-emerald-700">Coverage & dimensions</p>
            </CardContent>
          </Card>

          <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <Lightbulb className="w-5 h-5 text-purple-600" />
                <Sparkles className="w-4 h-4 text-purple-600" />
              </div>
              <p className="text-xs font-semibold text-purple-900">Keyword Enhancement</p>
              <p className="text-[11px] text-purple-700">Missing terms & data</p>
            </CardContent>
          </Card>

          <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <Award className="w-5 h-5 text-amber-600" />
                <span className="text-xs font-bold text-amber-900">0-10</span>
              </div>
              <p className="text-xs font-semibold text-amber-900">Score Estimate</p>
              <p className="text-[11px] text-amber-700">UPSC-style marking</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-2 items-start">
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-center gap-3">
                <div className="bg-orange-100 p-2 rounded-lg">
                  <FileText className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Submit Your GS-I Answer</h2>
                  <p className="text-xs text-slate-600">Paste your GS-I question and answer for evaluation</p>
                </div>
              </div>

              <form className="space-y-4" onSubmit={handleEvaluate}>
                {/* Input Mode Toggle */}
                <div className="space-y-2">
                  <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-700">INPUT METHOD</p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={evalInputMode === "type" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setEvalInputMode("type")}
                      className={evalInputMode === "type" ? "bg-orange-500 hover:bg-orange-600" : ""}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Type Answer
                    </Button>
                    <Button
                      type="button"
                      variant={evalInputMode === "handwritten" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setEvalInputMode("handwritten")}
                      className={evalInputMode === "handwritten" ? "bg-purple-500 hover:bg-purple-600" : ""}
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Upload Handwritten
                    </Button>
                  </div>
                </div>

                {evalInputMode === "type" ? (
                  <>
                    <div className="space-y-2">
                      <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-700">QUESTION</p>
                      <Textarea
                        rows={3}
                        value={evalQuestion}
                        onChange={(e) => setEvalQuestion(e.target.value)}
                        placeholder="Paste the GS-I question you attempted..."
                        required
                        className="text-sm resize-none"
                      />
                    </div>

                    <div className="space-y-2">
                      <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-700">YOUR ANSWER</p>
                      <Textarea
                        rows={10}
                        value={evalAnswer}
                        onChange={(e) => setEvalAnswer(e.target.value)}
                        placeholder="Paste or type your full GS-I answer here..."
                        required
                        className="text-sm resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-lg"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                          Evaluating...
                        </>
                      ) : (
                        <>
                          <Target className="w-4 h-4 mr-2" />
                          Evaluate My GS-I Answer
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <HandwrittenUpload
                    marks={10}
                    suite="GS1"
                    onEvaluationComplete={(data) => {
                      setEvaluation(data);
                      if (data.extracted_question) setEvalQuestion(data.extracted_question);
                      if (data.extracted_answer) setEvalAnswer(data.extracted_answer);
                      toast({ description: "✅ Handwritten GS-I answer evaluated!" });
                    }}
                    onExtractedData={(data) => {
                      if (data.question) setEvalQuestion(data.question);
                      if (data.answer) setEvalAnswer(data.answer);
                    }}
                  />
                )}

                <div className="space-y-2">
                  <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-700">MARKS</p>
                  <div className="flex gap-2">
                    {[10, 15, 20].map((m) => (
                      <Button
                        key={m}
                        type="button"
                        variant={marks === m ? "default" : "outline"}
                        size="sm"
                        onClick={() => setMarks(m)}
                        className={marks === m ? "bg-orange-500 hover:bg-orange-600" : ""}
                      >
                        {m}
                      </Button>
                    ))}
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Evaluation Results Viewer */}
          {!evaluation && !loading && (
            <Card className="border-dashed border-slate-300 bg-slate-50/50">
              <CardContent className="p-8 flex flex-col justify-center items-center h-full text-center space-y-4">
                <div className="bg-gradient-to-br from-orange-100 to-red-100 p-4 rounded-full">
                  <Target className="w-8 h-8 text-orange-600" />
                </div>
                <div className="space-y-2">
                  <p className="text-base font-bold text-slate-900">Your GS-I Evaluation Will Appear Here</p>
                  <p className="text-sm text-slate-600 max-w-md">
                    Submit your GS-I answer to receive detailed AI-powered feedback
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3 w-full max-w-sm text-left">
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <CircleCheck className="w-4 h-4 text-emerald-600 mb-1" />
                    <p className="text-[11px] font-semibold text-slate-900">Structure Check</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <TrendingUp className="w-4 h-4 text-blue-600 mb-1" />
                    <p className="text-[11px] font-semibold text-slate-900">Depth Analysis</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <Lightbulb className="w-4 h-4 text-amber-600 mb-1" />
                    <p className="text-[11px] font-semibold text-slate-900">Keywords</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <Award className="w-4 h-4 text-purple-600 mb-1" />
                    <p className="text-[11px] font-semibold text-slate-900">Score (0-10)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {evaluation && (
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardContent className="p-6 space-y-5">
                {/* Header with Score */}
                <div className="flex items-center justify-between pb-4 border-b border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-br from-orange-100 to-red-100 p-2 rounded-lg">
                      <Award className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-slate-900">GS-I Evaluation Results</h2>
                      <p className="text-xs text-slate-600">Examiner-aligned GS-I feedback</p>
                    </div>
                  </div>
                  {typeof evaluation.score_estimate === "number" && (
                    <div className="text-center">
                      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-xl">
                        <p className="text-xs font-semibold">Estimated Score</p>
                        <p className="text-2xl font-bold">{(evaluation.score_estimate / marks * 10).toFixed(1)}/10</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Detailed Scores - NEW */}
                {evaluation.scores && Object.keys(evaluation.scores).length > 0 && (
                  <Card className="border-slate-200 bg-white">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-slate-600" />
                        <p className="text-sm font-bold text-slate-900">📊 Detailed Scores</p>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {Object.entries(evaluation.scores).map(([criterion, score]) => (
                          <div key={criterion} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                            <span className="text-xs font-medium text-slate-700 capitalize">
                              {criterion.replace(/_/g, " ")}
                            </span>
                            <Badge variant="outline" className="text-xs">{score}/10</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Strengths & Weaknesses - NEW */}
                {(evaluation.strongest_areas?.length > 0 || evaluation.weakest_areas?.length > 0) && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {evaluation.strongest_areas?.length > 0 && (
                      <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
                        <CardContent className="p-4 space-y-2">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                            <p className="text-sm font-bold text-green-900">Strongest Areas</p>
                          </div>
                          <ul className="space-y-1.5">
                            {evaluation.strongest_areas.map((area, idx) => (
                              <li key={idx} className="text-xs text-green-800 flex items-start gap-2">
                                <span className="text-green-600">✓</span>
                                <span>{area}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    )}

                    {evaluation.weakest_areas?.length > 0 && (
                      <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                        <CardContent className="p-4 space-y-2">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-600" />
                            <p className="text-sm font-bold text-amber-900">Weakest Areas</p>
                          </div>
                          <ul className="space-y-1.5">
                            {evaluation.weakest_areas.map((area, idx) => (
                              <li key={idx} className="text-xs text-amber-800 flex items-start gap-2">
                                <span className="text-amber-600">⚠</span>
                                <span>{area}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}

                {/* Structure Feedback */}
                {evaluation.structure_feedback?.length > 0 && (
                  <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-blue-600" />
                        <p className="text-sm font-bold text-blue-900">Structure & Flow</p>
                      </div>
                      <ul className="space-y-1.5">
                        {evaluation.structure_feedback.map((p, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                            <CircleCheck className="w-3 h-3 text-blue-600 flex-shrink-0 mt-0.5" />
                            <span>{p.comment}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Depth Feedback */}
                {evaluation.depth_feedback?.length > 0 && (
                  <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-emerald-600" />
                        <p className="text-sm font-bold text-emerald-900">Depth & Coverage</p>
                      </div>
                      <ul className="space-y-1.5">
                        {evaluation.depth_feedback.map((p, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                            <TrendingUp className="w-3 h-3 text-emerald-600 flex-shrink-0 mt-0.5" />
                            <span>{p.comment}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Keywords */}
                {evaluation.keyword_suggestions?.length > 0 && (
                  <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-purple-600" />
                        <p className="text-sm font-bold text-purple-900">Keywords to Add</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {evaluation.keyword_suggestions.map((keyword, i) => (
                          <Badge key={i} className="bg-purple-100 text-purple-900 text-xs border-purple-300">
                            {keyword}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Missing Examples */}
                {evaluation.missing_examples?.length > 0 && (
                  <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                        <p className="text-sm font-bold text-amber-900">Missing Data & Examples</p>
                      </div>
                      <ul className="space-y-1.5">
                        {evaluation.missing_examples.map((example, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                            <span className="text-amber-600 flex-shrink-0">•</span>
                            <span>{example}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Missing Elements (Detailed) - NEW */}
                {evaluation.missing_elements && (evaluation.missing_elements.keywords?.length > 0 || evaluation.missing_elements.data?.length > 0 || evaluation.missing_elements.examples?.length > 0) && (
                  <Card className="border-slate-200 bg-white">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <Search className="w-4 h-4 text-slate-600" />
                        <p className="text-sm font-bold text-slate-900">🔍 Missing Elements</p>
                      </div>
                      <div className="grid md:grid-cols-3 gap-4">
                        {evaluation.missing_elements.keywords?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-slate-700 mb-2">Keywords</p>
                            <div className="flex flex-wrap gap-1.5">
                              {evaluation.missing_elements.keywords.map((kw, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">{kw}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {evaluation.missing_elements.data?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-slate-700 mb-2">Data/Statistics</p>
                            <ul className="text-xs text-slate-600 space-y-1">
                              {evaluation.missing_elements.data.map((d, idx) => (
                                <li key={idx}>• {d}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {evaluation.missing_elements.examples?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-slate-700 mb-2">Examples</p>
                            <ul className="text-xs text-slate-600 space-y-1">
                              {evaluation.missing_elements.examples.map((ex, idx) => (
                                <li key={idx}>• {ex}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* 10-Point Improvement Plan - NEW */}
                {evaluation.improvement_plan?.length > 0 && (
                  <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-indigo-600" />
                        <p className="text-sm font-bold text-indigo-900">🎯 10-Point Improvement Plan</p>
                      </div>
                      <ol className="space-y-2">
                        {evaluation.improvement_plan.map((point, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-xs text-slate-700">
                            <span className="flex-shrink-0 w-5 h-5 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-[10px] font-bold">
                              {idx + 1}
                            </span>
                            <span>{point.replace(/^\d+\.\s*/, '')}</span>
                          </li>
                        ))}
                      </ol>
                    </CardContent>
                  </Card>
                )}

                {/* Rewrite Suggestions - NEW */}
                {evaluation.rewrite_suggestions && (evaluation.rewrite_suggestions.introduction || evaluation.rewrite_suggestions.key_paragraph) && (
                  <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <Pencil className="w-4 h-4 text-purple-600" />
                        <p className="text-sm font-bold text-purple-900">✏️ Rewrite Suggestions</p>
                      </div>
                      {evaluation.rewrite_suggestions.introduction && (
                        <div className="space-y-1">
                          <p className="text-xs font-semibold text-purple-700">Improved Introduction</p>
                          <p className="text-xs text-slate-700 bg-white rounded-lg p-3 border border-purple-200">
                            {evaluation.rewrite_suggestions.introduction}
                          </p>
                        </div>
                      )}
                      {evaluation.rewrite_suggestions.key_paragraph && (
                        <div className="space-y-1">
                          <p className="text-xs font-semibold text-purple-700">Rewritten Key Paragraph</p>
                          <p className="text-xs text-slate-700 bg-white rounded-lg p-3 border border-purple-200">
                            {evaluation.rewrite_suggestions.key_paragraph}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Better Intro */}
                {evaluation.better_intro && (
                  <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-indigo-600" />
                        <p className="text-sm font-bold text-indigo-900">Suggested Better Introduction</p>
                      </div>
                      <p className="text-xs text-slate-700 bg-white rounded-lg p-3 border border-indigo-200">
                        {evaluation.better_intro}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Better Conclusion */}
                {evaluation.better_conclusion && (
                  <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-indigo-600" />
                        <p className="text-sm font-bold text-indigo-900">Suggested Better Conclusion</p>
                      </div>
                      <p className="text-xs text-slate-700 bg-white rounded-lg p-3 border border-indigo-200">
                        {evaluation.better_conclusion}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Improved Answer */}
                {evaluation.improved_answer && (
                  <Card className="border-slate-200 bg-white">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Award className="w-4 h-4 text-slate-600" />
                        <p className="text-sm font-bold text-slate-900">Improved Model Answer</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 max-h-96 overflow-y-auto text-xs text-slate-700 space-y-2">
                        {evaluation.improved_answer.split("\n").map((line, i) => (
                          <p key={i}>{line}</p>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Meta Tips */}
                {evaluation.meta_tips?.length > 0 && (
                  <Card className="border-rose-200 bg-gradient-to-br from-rose-50 to-pink-50">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-rose-600" />
                        <p className="text-sm font-bold text-rose-900">Meta Tips for Improvement</p>
                      </div>
                      <ul className="space-y-1.5">
                        {evaluation.meta_tips.map((tip, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                            <span className="text-rose-600 flex-shrink-0 font-bold">→</span>
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* AI Output Disclaimer - MANDATORY on answer evaluation screens */}
                <AIOutputDisclaimer variant="small" />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // EXPECTED QUESTIONS VIEW
  if (mode === "expected-questions") {
    // PRO-only feature check
    if (!isPro) {
      return (
        <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
          <Button variant="outline" onClick={() => setMode("home")}>← Back to GS-I Suite</Button>
          <Card className="border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50">
            <CardContent className="p-8 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
                <Lock className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Expected Questions is PRO-Only</h2>
              <p className="text-slate-600 max-w-md mx-auto">
                Get Smart predicted questions with probability analysis, PYQ hooks, and current affairs links. Upgrade to PRO to unlock this powerful feature.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                <Button 
                  className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-8"
                  onClick={() => window.location.href = '/upgrade'}
                >
                  Upgrade to PRO
                </Button>
                <Button variant="outline" onClick={() => setMode("home")}>
                  Back to Suite
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-4">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setMode("home")}>← Back to GS-I Suite</Button>
          <Badge className="px-4 py-2 text-sm bg-gradient-to-r from-amber-500 to-orange-500 text-white">⭐ PRO</Badge>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold text-slate-900">🏛️ GS-I Expected Questions</h1>
            
            <p className="text-xs text-slate-600">
              Smart predicted GS-I questions based on past 1 year Current Affairs & PYQ patterns
            </p>
          </div>
        </div>

        {expectedData && (
          <Card className="border-slate-200 bg-white/90">
            <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <p className="flex-1 text-[11px] text-slate-700">Based on latest current affairs and GS-I syllabus</p>
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-slate-600 text-[11px]">Subject:</label>
                <select
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] text-slate-800"
                >
                  {GS1_SUBJECTS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <Button
                  size="sm"
                  onClick={fetchExpectedQuestions}
                  disabled={loading}
                  className="text-[11px] rounded-full px-4 py-1.5 bg-gradient-to-r from-orange-500 to-red-500 text-white"
                >
                  {loading ? "Refreshing..." : "Refresh"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading && (
          <Card className="border-slate-200 bg-white/90">
            <CardContent className="p-8 text-center">
              <div className="inline-block w-10 h-10 border-4 border-t-orange-600 border-r-transparent border-b-orange-600 border-l-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-sm text-slate-600">Loading GS-I questions...</p>
            </CardContent>
          </Card>
        )}

        {!loading && expectedData && expectedData.questions && expectedData.questions.length > 0 && (
          <div className="space-y-3">
            {expectedData.questions.map((q, idx) => {
              const qText = typeof q === 'string' ? q : (q.question || "");
              const qId = q.id || `q-${idx}`;
              const isPracticed = practicedIds.includes(qId);

              return (
                <Card key={idx} className="border-slate-200 bg-white/90 hover:shadow-md transition-shadow">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center text-[11px] font-semibold text-orange-800">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        {q.probability_tag && (
                          <Badge variant="outline" className={`text-[10px] mb-2 ${q.probability_tag === '🔒 PRO' ? 'bg-slate-100 text-slate-500 border-slate-300' : 'bg-amber-50 text-amber-800 border-amber-300'}`}>
                            {q.probability_tag}
                          </Badge>
                        )}
                        {q.theme && (
                          <p className="text-[10px] text-slate-500 mb-1 font-medium">Theme: {q.theme}</p>
                        )}
                        <p className="text-sm text-slate-800 font-medium mb-2">{qText}</p>
                        
                        {q.why_likely && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 mb-2">
                            <p className="text-[10px] font-semibold text-blue-900 mb-1">💡 Why this question is likely:</p>
                            <p className="text-[11px] text-blue-800">{q.why_likely}</p>
                          </div>
                        )}
                        
                        {q.pyq_hook && (
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-2.5 mb-2">
                            <p className="text-[10px] font-semibold text-purple-900 mb-1">📚 PYQ Pattern:</p>
                            <p className="text-[11px] text-purple-800">{q.pyq_hook}</p>
                          </div>
                        )}
                        
                        {q.current_link && (
                          <div className="text-[11px] text-green-700 mb-2">
                            <span className="font-semibold">🔗 Current Link:</span> {q.current_link}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSendToMains(qText, qId)}
                          className="text-[10px] px-2 py-1 h-auto"
                        >
                          Send to GS-I
                        </Button>
                        {!isPracticed && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleMarkPracticed(qId)}
                            className="text-[10px] px-2 py-1 h-auto text-green-700 hover:text-green-800 hover:bg-green-50"
                          >
                            Mark Practiced
                          </Button>
                        )}
                        {isPracticed && (
                          <span className="text-[10px] text-green-700 px-2 py-1">✓ Practiced</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // PYQ PATTERN VIEW
  if (mode === "pyq-pattern") {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setMode("home")}>← Back to GS-I Suite</Button>
          <Badge className="px-4 py-2 text-sm">{isPro ? "⭐ PRO" : "🆓 FREE"}</Badge>
        </div>

        <div className="bg-gradient-to-r from-rose-500 via-pink-500 to-red-500 rounded-2xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
                <TrendingUp className="w-8 h-8" />
                GS-I PYQ Pattern Analysis
              </h1>
              <p className="text-rose-50">
                Discover 20+ years of GS-I question trends, frequency analysis & topic forecasting
              </p>
            </div>
          </div>
        </div>

        {/* Controls */}
        <Card className="border-slate-200 bg-white">
          <CardContent className="p-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">SELECT GS-I SUBJECT</label>
                <select
                  value={pyqSubject}
                  onChange={(e) => setPyqSubject(e.target.value)}
                  className="w-full rounded-md border-2 border-slate-200 bg-white px-4 py-2 text-sm text-slate-800 focus:border-rose-400 focus:outline-none"
                >
                  {GS1_SUBJECTS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">YEAR RANGE</label>
                <div className="flex gap-2">
                  {["5", "10", "15", "20"].map((years) => (
                    <Button
                      key={years}
                      type="button"
                      variant={pyqYearRange === years ? "default" : "outline"}
                      size="sm"
                      onClick={() => setPyqYearRange(years)}
                      className={pyqYearRange === years ? "bg-rose-500 hover:bg-rose-600" : ""}
                    >
                      {years} Years
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            <Button
              onClick={fetchPyqPattern}
              disabled={loading}
              className="w-full mt-4 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white font-semibold"
            >
              {loading ? "Analyzing PYQ Patterns..." : "🔍 Analyze GS-I PYQ Patterns"}
            </Button>
          </CardContent>
        </Card>

        {/* Loading State */}
        {loading && (
          <Card className="border-slate-200 bg-white">
            <CardContent className="p-8 text-center">
              <div className="inline-block w-10 h-10 border-4 border-t-rose-600 border-r-transparent border-b-rose-600 border-l-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-sm text-slate-600">Analyzing {pyqYearRange} years of GS-I PYQ data...</p>
            </CardContent>
          </Card>
        )}

        {/* PYQ Pattern Display */}
        {!loading && pyqData && (
          <div className="space-y-6">
            {/* Summary Stats */}
            {pyqData.summary && (
              <div className="grid gap-4 md:grid-cols-4">
                <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-blue-900">{pyqData.summary.total_questions || 0}</p>
                    <p className="text-xs text-blue-700 mt-1">Total Questions</p>
                  </CardContent>
                </Card>
                <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-purple-900">{pyqData.summary.avg_per_year || 0}</p>
                    <p className="text-xs text-purple-700 mt-1">Avg Questions/Year</p>
                  </CardContent>
                </Card>
                <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-emerald-900">{pyqData.summary.most_frequent_topic || "N/A"}</p>
                    <p className="text-xs text-emerald-700 mt-1">Most Frequent Topic</p>
                  </CardContent>
                </Card>
                <Card className="border-rose-200 bg-gradient-to-br from-rose-50 to-pink-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-rose-900">{pyqData.summary.trend || "↗️"}</p>
                    <p className="text-xs text-rose-700 mt-1">Trend Direction</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Topic Frequency Analysis */}
            {pyqData.topic_frequency && pyqData.topic_frequency.length > 0 && (
              <Card className="border-slate-200 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-rose-600" />
                    Topic Frequency Analysis
                  </h3>
                  <div className="space-y-3">
                    {pyqData.topic_frequency.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-rose-100 text-rose-900 rounded-full flex items-center justify-center text-xs font-bold">
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-semibold text-slate-900">{item.topic}</span>
                            <span className="text-sm font-bold text-rose-600">{item.count} Qs</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-rose-500 to-pink-500"
                              style={{ width: `${(item.count / (pyqData.summary?.total_questions || 100)) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Year-wise Trends */}
            {pyqData.year_wise && pyqData.year_wise.length > 0 && (
              <Card className="border-slate-200 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-rose-600" />
                    Year-wise Question Trends
                  </h3>
                  <div className="space-y-2">
                    {pyqData.year_wise.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-sm font-semibold text-slate-900">{item.year}</span>
                        <Badge variant="secondary">{item.count} Questions</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Actual PYQs */}
            {pyqData.questions && pyqData.questions.length > 0 && (
              <Card className="border-slate-200 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-rose-600" />
                    Previous Year Questions ({pyqData.questions.length})
                  </h3>
                  <div className="space-y-3">
                    {pyqData.questions.map((q, idx) => (
                      <Card key={idx} className="border-rose-200 bg-rose-50/50 hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                              <Badge variant="outline" className="bg-rose-100 text-rose-900 border-rose-300">
                                {q.year}
                              </Badge>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-slate-800 font-medium mb-2">{q.question}</p>
                              {q.marks && (
                                <Badge variant="secondary" className="text-xs">{q.marks} marks</Badge>
                              )}
                              {q.topic && (
                                <Badge variant="secondary" className="text-xs ml-2 bg-purple-100 text-purple-900">
                                  {q.topic}
                                </Badge>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setQuestion(q.question);
                                if (q.marks) setMarks(q.marks);
                                setMode("answer-writing");
                              }}
                              className="text-xs"
                            >
                              Practice
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Insights */}
            {pyqData.insights && pyqData.insights.length > 0 && (
              <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-amber-900 mb-4 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-amber-600" />
                    Key Insights & Forecasting
                  </h3>
                  <ul className="space-y-2">
                    {pyqData.insights.map((insight, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-800">
                        <span className="text-amber-600 flex-shrink-0 mt-0.5">💡</span>
                        <span>{insight}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Empty State */}
        {!loading && !pyqData && (
          <Card className="border-slate-200 bg-white">
            <CardContent className="p-12 text-center">
              <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-base font-bold text-slate-900 mb-2">No PYQ Analysis Yet</p>
              <p className="text-sm text-slate-600">
                Select a GS-I subject and year range, then click &ldquo;Analyze GS-I PYQ Patterns&rdquo;
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return null;
};

export default GS1SuitePage;
